import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, Navigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';

interface AuthPageProps {
  initialMode?: 'login' | 'signup';
}

const AuthPage: React.FC<AuthPageProps> = ({ initialMode = 'login' }) => {
  const [isLogin, setIsLogin] = useState(initialMode === 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { user, loginWithEmail, registerWithEmail, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in (but not in admin)
  if (user) {
    return <Navigate to="/profile" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await loginWithEmail(email, password);
      } else {
        await registerWithEmail(email, password, name);
      }
      navigate('/profile');
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Failed to authenticate');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
     try {
        await signInWithGoogle();
     } catch (err) {
        console.error(err);
     }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col pt-24 pb-12 px-4">
      <div className="max-w-md w-full mx-auto flex-grow flex flex-col justify-center">
         
         <div className="text-center mb-12">
            <h1 className="font-heading text-3xl font-black tracking-tighter mb-2">
                {isLogin ? 'WELCOME BACK' : 'KEEP THE ARCHIVE'}
            </h1>
            <p className="font-body text-sm text-gray-500">
                {isLogin ? 'Sign in to access your profile' : 'An account records your claims.'}
            </p>
         </div>

         <div className="bg-white p-8 border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] rounded-xl relative overflow-hidden">
            {/* Top Shine */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"></div>

            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                {!isLogin && (
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Full Name</label>
                        <input
                            type="text"
                            required
                            className="w-full bg-gray-50 border border-gray-200 p-3 rounded-md focus:outline-none focus:border-black transition-colors"
                            placeholder="Your name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </div>
                )}
                
                <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Email Address</label>
                    <input
                        type="email"
                        required
                        className="w-full bg-gray-50 border border-gray-200 p-3 rounded-md focus:outline-none focus:border-black transition-colors"
                        placeholder="skater@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                </div>

                <div className="relative">
                    <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Password</label>
                    <input
                        type={showPassword ? "text" : "password"}
                        required
                        className="w-full bg-gray-50 border border-gray-200 p-3 rounded-md focus:outline-none focus:border-black transition-colors"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />
                    <button 
                        type="button" 
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-[34px] text-gray-400 hover:text-black"
                    >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                </div>

                {error && <p className="text-red-500 text-xs text-center">{error}</p>}

                <button 
                    type="submit" 
                    disabled={loading}
                    className="w-full bg-black text-white font-heading font-black tracking-widest py-4 rounded-md hover:bg-gray-900 transition-colors disabled:opacity-50"
                >
                    {loading ? 'PROCESSING...' : (isLogin ? 'SIGN IN' : 'CREATE ACCOUNT')}
                </button>
            </form>

            <div className="my-8 flex items-center gap-4">
                <div className="h-px bg-gray-100 flex-1"></div>
                <span className="text-xs text-gray-400 font-mono">OR</span>
                <div className="h-px bg-gray-100 flex-1"></div>
            </div>

            <button 
                type="button"
                onClick={handleGoogle}
                className="w-full bg-white border border-gray-200 text-gray-700 font-heading font-bold tracking-widest py-3 rounded-md hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
            >
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="w-5 h-5" alt="Google" />
                CONTINUE WITH GOOGLE
            </button>
         </div>

         <div className="text-center mt-8">
            <button 
                onClick={() => setIsLogin(!isLogin)}
                className="text-sm text-gray-500 hover:text-black transition-colors"
            >
                {isLogin ? "Don't have an account? " : "Already have an account? "}
                <span className="font-bold underline">{isLogin ? "Join now" : "Log in"}</span>
            </button>
         </div>
      </div>
    </div>
  );
};

export default AuthPage;
